/* eslint-disable @typescript-eslint/no-explicit-any */

// Solana's default RPC stack supports identifying recently paid priority fees globally and per writeable account. Currently, this provides a single number per slot to
// indicate the minimum priority fee amount. Returning only the minimum priority fee limits the ability of RPC consumers to more clearly identify what priority fees to use in
// their transactions.

// To address this, we have added a percentile parameter to the Solana JSON RPC `getRecentPrioritizationFees` method that provides you better flexibility to estimate previous
//  priority fees that were paid and, thus, what you might want to add to your transactions.
// https://docs.triton.one/chains/solana/improved-priority-fees-api
// https://github.com/rpcpool/solana-prioritization-fees-api/
import type {
  Connection,
  GetRecentPrioritizationFeesConfig,
  RecentPrioritizationFees,
} from "@solana/web3.js";

export const enum PrioritizationFeeLevels {
  LOW = 2500,
  MEDIUM = 5000,
  HIGH = 7500,
  MAX = 10000,
}

interface GetRecentPrioritizationFeesByPercentileConfig
  extends GetRecentPrioritizationFeesConfig {
  percentile?: PrioritizationFeeLevels | number;
  fallback?: boolean;
}

interface RpcResponse {
  jsonrpc: string;
  id?: string;
  result?: [];
  error?: any;
}

export const LAMPORTS = 10 ** 9;
const units = 200000;

const convertMicroLamportsPerUnitToLamports = (microLamports: number) =>
  (microLamports * units) / 10 ** 6;

const getRecentPrioritizationFeesFromRpc = async (
  config: any,
  rpcRequest: any
): Promise<RpcResponse> => {
  const accounts = config?.lockedWritableAccounts?.map(
    (key: { toBase58: () => any }) => key.toBase58()
  );
  const args = accounts?.length ? [accounts] : [[]];
  if (config.percentile) {
    args.push({ percentile: config.percentile });
  }

  return rpcRequest("getRecentPrioritizationFees", args);
};

export const getRecentPrioritizationFeesByPercentile = async (
  connection: Connection,
  config: GetRecentPrioritizationFeesByPercentileConfig,
  slotsToReturn?: number
): Promise<RecentPrioritizationFees[]> => {
  const { fallback = true, lockedWritableAccounts = [] } = config || {};
  const slots =
    slotsToReturn && Number.isInteger(slotsToReturn) ? slotsToReturn : -1;

  const promises = [];

  let tritonRpcResponse: RpcResponse | undefined;
  let fallbackRpcResponse: RpcResponse | undefined;

  // @solana/web3.js uses the private method `_rpcRequest` internally to make RPC requests which is not exposed by TypeScript
  // it is available in JavaScript, however, TypeScript enforces it as unavailable and complains, the following line is a workaround

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  /* @ts-ignore */
  // eslint-disable-next-line no-underscore-dangle
  const rpcRequest = connection._rpcRequest;

  // to save fallback roundtrips if your RPC is not Triton, both RPCs are called in parallel to minimize latency
  // if your RPC is Triton, we set percentile to config
  promises.push(
    getRecentPrioritizationFeesFromRpc(config, rpcRequest).then((result) => {
      tritonRpcResponse = result;
    })
  );

  // if your RPC isn't Triton, we don't set percentile to config. Because It isn't work for other nodes
  if (fallback) {
    promises.push(
      getRecentPrioritizationFeesFromRpc(
        { lockedWritableAccounts },
        rpcRequest
      ).then((result) => {
        fallbackRpcResponse = result;
      })
    );
  }

  await Promise.all(promises);

  let recentPrioritizationFees: RecentPrioritizationFees[] = [];
  if (tritonRpcResponse?.result) {
    recentPrioritizationFees = tritonRpcResponse.result;
  }
  if (fallbackRpcResponse?.result && !tritonRpcResponse?.result) {
    recentPrioritizationFees = fallbackRpcResponse.result;
  }

  // sort the prioritization fees by slot
  recentPrioritizationFees.sort((a, b) => a.slot - b.slot);

  // return the first n prioritization fees
  if (slots > 0) return recentPrioritizationFees.slice(0, slots);

  return recentPrioritizationFees;
};

export const getMediumPrioritizationFeeByPercentile = async (
  connection: Connection,
  config: GetRecentPrioritizationFeesByPercentileConfig,
  slotsToReturn?: number
): Promise<number> => {
  const recentPrioritizationFees =
    await getRecentPrioritizationFeesByPercentile(
      connection,
      config,
      slotsToReturn
    );

  recentPrioritizationFees.sort(
    (a, b) => a.prioritizationFee - b.prioritizationFee
  );

  const half = Math.floor(recentPrioritizationFees.length / 2);

  if (recentPrioritizationFees.length % 2) {
    return recentPrioritizationFees[half].prioritizationFee;
  }

  return convertMicroLamportsPerUnitToLamports(
    Math.ceil(
      (recentPrioritizationFees[half - 1].prioritizationFee +
        recentPrioritizationFees[half].prioritizationFee) /
        2
    )
  );
};

export const getMaxPrioritizationFeeByPercentile = async (
  connection: Connection,
  config: GetRecentPrioritizationFeesByPercentileConfig,
  slotsToReturn?: number
): Promise<number> => {
  const recentPrioritizationFees =
    await getRecentPrioritizationFeesByPercentile(
      connection,
      config,
      slotsToReturn
    );

  const maxPriorityFee = recentPrioritizationFees.reduce((max, current) => {
    return current.prioritizationFee > max.prioritizationFee ? current : max;
  });

  return convertMicroLamportsPerUnitToLamports(
    maxPriorityFee.prioritizationFee
  );
};

export const getMinPrioritizationFeeByPercentile = async (
  connection: Connection,
  config: GetRecentPrioritizationFeesByPercentileConfig,
  slotsToReturn?: number
): Promise<number> => {
  const recentPrioritizationFees =
    await getRecentPrioritizationFeesByPercentile(
      connection,
      config,
      slotsToReturn
    );

  const minPriorityFee = recentPrioritizationFees.reduce((min, current) => {
    return current.prioritizationFee < min.prioritizationFee ? current : min;
  });

  return convertMicroLamportsPerUnitToLamports(
    minPriorityFee.prioritizationFee
  );
};
